import { useCallback, useMemo } from "react"
import { capitalize, get } from "lodash"
import { useAnalytics } from "connected-components/analytics"
import { usePositionSetBetaInstrument } from "connected-components/asset-view/helpers"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import { useAssetViewHeaderHandlers } from "connected-components/asset-view/hooks/use-asset-view-header-handlers"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import { useNormalPerformancePricingRefreshWarning } from "gatherers/hooks/use-normal-performance-pricing-refresh-warning"
import { ONE_DAY } from "helpers/dates"
import { getPerformanceFilterInstrumentColumns } from "helpers/metrics"
import { METRIC_TYPES } from "helpers/metrics/constants"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { ATTRIBUTION_METHODS } from "helpers/risk"
import { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { DESC } from "helpers/sorts"
import ContributorsTable from "renderers/contributors-datagrid"
import { useModelBetaFields } from "resources/beta"
import { ClassificationMeta } from "resources/classifications"
import { ModelMetadata } from "resources/models"
import { useSourceSummaryMetricIds } from "connected-components/asset-view/hooks/use-source-displayed-metrics"
import { useCompositionVisibility } from "connected-components/asset-view/hooks/use-source-displayed-metric-ids"
import type { SourceMetricIdsChangeHandler } from "connected-components/asset-view/hooks/use-source-displayed-metric-ids"
import { useIsOpPro } from "../../../../resources/account"

interface AssetViewSummaryProps {
  modelMetadata: ModelMetadata
  instrumentsWithMetadata: Record<string, InstrumentWithMetadata>
  highlightedRowIds: string[]
  disableActiveTrading: boolean
  instrumentColumns: string[]
  classifications: ClassificationMeta[]
  flattenedMetrics?: Record<string, unknown>
  onMetricsChange?: SourceMetricIdsChangeHandler
}

export default function AssetViewSummary({
  modelMetadata,
  instrumentsWithMetadata,
  highlightedRowIds,
  disableActiveTrading,
  instrumentColumns,
  classifications,
  flattenedMetrics,
  onMetricsChange,
}: AssetViewSummaryProps) {
  const contextData = useAssetViewData()
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    isIntraday,
    isEndVsStartDateComparison,
    modelId,
    period,
    performanceEndDate,
    performanceStartDate,
    startDate,
    endDate,
  } = contextData

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({
    track: analytics.track,
  }) as {
    handleSortChanged: (sortMetricId: string, sortDirection: string) => void
    handleTableNavigationChanged: (tableLevels: string[]) => void
  }

  const isOpPro = useIsOpPro()
  const [modelBetaFieldsResult] = useModelBetaFields(modelId)
  const modelBetaFields = useMemo(() => Array.isArray(modelBetaFieldsResult) ? modelBetaFieldsResult : [], [modelBetaFieldsResult])
  const betaInstrument = usePositionSetBetaInstrument(instrumentsWithMetadata.original, modelMetadata)
  const getBetaDataPath = useCallback((path: string) => {
    return `beta.${path}`
  }, [])

  const apiStartDate = period === ONE_DAY ? performanceEndDate : performanceStartDate
  const attributionMethod = contextBase ? ATTRIBUTION_METHODS.MCTE : ATTRIBUTION_METHODS.MCAR

  const gatherers = useTemplateGatherers(contextData, "summary", {
    classifications,
    betaInstrument,
    attributionMethod,
    betaFields: modelBetaFields,
    from: apiStartDate,
    to: performanceEndDate,
    selectedMetricDisplay: METRIC_TYPES.CURRENCY_PERFORMANCE,
  })

  useNormalPerformancePricingRefreshWarning({ gatherers, startDate, endDate })

  const hasReferenceEquity = get(instrumentsWithMetadata?.original, "referenceEquity") != null

  const prependMetrics = useMemo(() => [{ id: "description" }], [])

  const metricsAndCategories = useMemo(() => [
    {
      category: "performance",
      selectedMetricDisplay: METRIC_TYPES.CURRENCY_PERFORMANCE,
      selectedCategory: "",
      filterInstrumentColumns: getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison),
      includeFactorColumns: false,
    },
    ...(!isOpPro ? [{
      category: "risk",
      selectedCategory: "",
      categoryLabel: "Summary Risk",
      includeFactorColumns: false,
    }] : []),
    {
      category: "beta",
      betaFields: modelBetaFields,
      betaInstrument,
      getBetaDataPath,
    },
  ], [
    isOpPro,
    isEndVsStartDateComparison,
    modelBetaFields,
    betaInstrument,
    getBetaDataPath,
  ])

  const summaryOptions = useMemo(
    () => ({ betaFields: modelBetaFields, betaInstrument, selectedMetricDisplay: METRIC_TYPES.CURRENCY_PERFORMANCE, isIntradayEnabled: isIntraday }),
    [modelBetaFields, betaInstrument, isIntraday],
  )

  const compositionVisibility = useCompositionVisibility(instrumentColumns, original)

  useSourceSummaryMetricIds({ flattenedMetrics, onMetricsChange, summaryOptions, compositionVisibility })

  const dashboardMetricsOptions = useMemo(() => ({
    instrumentColumns,
    isActive: !!contextBase,
    modelMetadata,
    instrumentsWithMetadata,
    setTradedPosition,
    isExpanded,
    disableActiveTrading,
    isEndVsStartDateComparison,
    hasReferenceEquity,
    prependMetrics,
  }), [
    instrumentColumns,
    contextBase,
    modelMetadata,
    instrumentsWithMetadata,
    setTradedPosition,
    isExpanded,
    disableActiveTrading,
    isEndVsStartDateComparison,
    hasReferenceEquity,
    prependMetrics,
  ])

  const metrics = useAssetViewDashboardMetrics(
    metricsAndCategories,
    original,
    rebalanced,
    comparison,
    dashboardMetricsOptions,
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0] as keyof typeof INSTRUMENTS].suffix

  return <ContributorsDataIntegrator
    instrumentsWithMetadata={instrumentsWithMetadata}
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    onModelProviderIds={undefined}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      isPositionAttributeGroup: !!groupBy?.isPositionAttribute,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      downloadKey: "assets.summary",
      downloadName: "Assets - Summary",
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      showSecurityName: true,
      idLabel: "Ticker",
    }}
  />
}
